import { IdentifierData } from './types';

export const Steps = {
  START: 'start',
  SYSTEM_SELECT: 'systemSelection',
  PRODUCT_TYPE_SELECT: 'productTypeSelection',
  PRODUCT_PARAMETERS: 'productParameters',
  SYSTEM_PARAMETERS: 'systemParameters',
  CONFIGURATOR: 'configurator',
};

export const getCurrentStep = (state) => {
  return state.ipco.currentStep;
};

export const isStartScreen = (state) => {
  return state.ipco.currentStep === Steps.START;
};

export const isProductParametersStep = (state) => {
  return state.ipco.currentStep === Steps.PRODUCT_PARAMETERS;
};

export const isSystemParametersStep = (state) => {
  return state.ipco.currentStep === Steps.SYSTEM_PARAMETERS;
};

export const isConfiguratorStep = (state) => {
  return state.ipco.currentStep === Steps.CONFIGURATOR;
};

export const isProductTypeSelectionStep = (state) => getCurrentStep(state) === Steps.PRODUCT_TYPE_SELECT;
export const isSystemSelectionStep = (state) => getCurrentStep(state) === Steps.SYSTEM_SELECT;

export const getSelectedSystem = (state) => state.ipco.selectedSystem;

export const getSelectedProductType = (state) => state.ipco.selectedProductType;

export const getSystems = (state) => state.ipco.systems;

export const getProductTypes = (state) =>
  state.ipco.productTypes.filter(({ system }) => system === state.ipco.selectedSystem?.identifier);

export const getProductParamValidations = (state) => state.ipco.productParamsValidation;

export const getProductParameterFormData = (state, productIdentifier?: string) => {
  if (!productIdentifier) {
    productIdentifier = getSelectedProductType(state)?.identifier;
  }
  return (productIdentifier && state.ipco.productParameterFormData[productIdentifier]) || null;
};

export const getProductParameterWarnings = (state) => state.ipco.productParameterWarnings;
export const isProductParameterWarningsApproved = (state) => state.ipco.productParameterWarningsApproved;

export const getBackgroundImage = (state) =>
  getSelectedProductType(state)?.backgroundImage || getSelectedSystem(state)?.backgroundImage;

export const getSelectedProductIdentifier = (state) => state.ipco.selectedProductType?.identifier;

export const isConfigurationLoaded = (state) => !!state.configurator.configuration.item.identifier;

export const isNextButtonVisible = (state) => {
  const stepValues = Object.values(Steps);
  return (
    stepValues.indexOf(state.ipco.currentStep) >= stepValues.indexOf(Steps.PRODUCT_TYPE_SELECT) &&
    getNextStep(state)
  );
};

export const isCurrentStepOrLater = (step) => (state) => {
  const stepValues = Object.values(Steps);
  return stepValues.indexOf(state.ipco?.currentStep) >= stepValues.indexOf(step);
};

export const isProductParamsValid = (state) => {
  const validations = state.ipco.productParamsValidation;
  const validationKeys = Object.keys(validations);
  return validationKeys.length > 0 && !validationKeys.some((validation) => validations[validation] === false);
};

export const validateSystemParameters = (state) => {
  const systemParameters = getSystemParameters(state);
  return (
    systemParameters.length > 0 &&
    !systemParameters.some(
      (component) => component.isMandatory && component.active && !component.selectedOptions?.length
    )
  );
};

// if we have custom data saved for every product param fields,and none of them are empty
// and all of them are valid, and the terms was accepted, then the step is completed
export const validateProjectParameters = (state) => {
  return isProductParamsValid(state);
};

export const isCurrentStepReady = (state) => {
  return isStepReady(getCurrentStep(state))(state);
};

const isSingleStepReady = (step, state) => {
  switch (step) {
    case Steps.PRODUCT_TYPE_SELECT:
      return !!getSelectedProductType(state);
    case Steps.SYSTEM_PARAMETERS:
      return validateSystemParameters(state);
    case Steps.PRODUCT_PARAMETERS:
      return validateProjectParameters(state);
    default:
      return true;
  }
};
export const isStepReady = (step) => (state) => {
  const stepValues = Object.values(Steps);
  const targetIndex = stepValues.indexOf(step);
  if (targetIndex === -1) {
    return false;
  }
  return stepValues.slice(0, targetIndex + 1).every((s) => isSingleStepReady(s, state));
};

export const isPreviousStepReady = (step) => (state) => {
  const stepValues = Object.values(Steps);
  const index = stepValues.indexOf(step);
  if (index <= 0) {
    return true;
  }
  const previousStep = stepValues[index - 1];
  return isStepReady(previousStep)(state);
};

export const getNextStep = (state) => {
  const stepValues = Object.values(Steps);
  return stepValues[stepValues.indexOf(state.ipco.currentStep) + 1];
};

export const getSystemParameters = (state) => state.ipco.systemParameters;

export const getSystemParametersWithSelectedOption = (state) => {
  const allComponents = state.configurator.configuration.optionclassifications;
  return getSystemParameters(state).map((component) => {
    const configurationComponent = allComponents.find(
      ({ identifier }) => identifier === component.identifier
    );
    return {
      ...component,
      selectedOptions:
        (configurationComponent?.selectedoptions && configurationComponent?.selectedoptions) || [],
    };
  });
};

export const getMenuTitle = (state, menuKey): string => {
  const firstItem = state.ipco.configuratorMenuOptions.find(({ identifier }) => identifier === menuKey)
    ?.elements?.[0];
  return getGroupTitle(getFirstComponent(firstItem));
};

const getFirstComponent = (menuRoot) =>
  menuRoot?.attributes ? menuRoot : menuRoot?.entries?.length && getFirstComponent(menuRoot.entries[0]);

const getGroupTitle = (component) =>
  component?.attributes?.find(({ identifier }) => identifier === 'group')?.valueTranslation;

export const getConfiguratorMenuOptions = (state) => {
  const result: IdentifierData[] = [];
  for (const menuGroup of state.ipco.configuratorMenuOptions) {
    const menuTitle = getGroupTitle(getFirstComponent(menuGroup.elements?.[0]));
    if (menuTitle) {
      result.push({
        identifier: menuGroup.identifier,
        title: menuTitle,
      });
    }
  }
  return result;
};

export const getCustomData = (state) => state.configurator.configuration.customdata;

export const getSelectedConfiguratorMenu = (state) => state.ipco.selectedConfiguratorMenu;

export const isSurroundingsMode = (configuratorMenu) => configuratorMenu === 'surroundings';

export const isSurroundingsModeActive = (state) => isSurroundingsMode(getSelectedConfiguratorMenu(state));

export const getConfiguratorMenuComponents = (state) => {
  const selectedMenu = getSelectedConfiguratorMenu(state);
  const result = state.ipco.configuratorMenuOptions.find(
    ({ identifier }) => identifier === selectedMenu
  )?.elements;

  if (Array.isArray(result)) {
    return result;
  } else if (typeof result === 'object') {
    return Object.values(result);
  }
  return [];
};

export const getSelectedComponent = (state) => state.ipco.selectedComponent;

export const getSelectedComponentWithOptions = (state) => {
  const component = getSelectedComponent(state);
  const allComponents = state.configurator.configuration.optionclassifications;
  const configurationComponent = component
    ? allComponents.find(({ identifier }) => identifier === component.identifier)
    : null;
  return component
    ? {
        ...component,
        selectedOptions:
          (configurationComponent?.selectedoptions && configurationComponent?.selectedoptions) || [],
      }
    : null;
};

export const getTotalPrice = (state) => state.configurator.calculation?.total;

export const getFormattedPrice = (state) => state.configurator.calculation?.totalFormatted;

export const getCoolingZones = (state) =>
  state.ipco.configuratorMenuOptions.find(({ isCoolingZone }) => isCoolingZone)?.elements;

export const getCoolingZoneComponents = (state) => {
  const components = getConfiguratorMenuComponents(state);
  return components ? components.filter((component) => component.identifier.includes('cooling_zone')) : [];
};

export const getLanguages = (state) => state.ipco.languages;

export const getCountries = (state) => state.ipco.countries;

export const isLoginRequired = (state) => state.ipco.loginRequired;

export const getAuthMessage = (state) => state.ipco.authMessage;

export const getDisabledOptions = (state) => state.ipco.disabledOptions;

export const getAccessDeniedError = (state) => state.ipco.accessDeniedError;

export const getUserFlags = (state) => state.ipco.userFlags;
